import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { ChevronDown, Menu, X, ArrowUpRight } from "lucide-react";
import ThemeToggle from "./ThemeToggle";
import SynqLogo from "../SynqLogo";
import { useAuth } from "../../contexts/AuthContext";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/solutions", label: "Solutions" },
  { to: "/resources", label: "Resources" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const PRODUCTS = [
  { to: "/products/projects", name: "SYNQ Projects", tag: "Project Intelligence Platform", active: true },
  { to: "/products/evolve", name: "SYNQ Evolve", tag: "Organizational Intelligence Platform", active: true },
];

const COMING_SOON = [
  { name: "SYNQ AI", tag: "Applied AI Intelligence" },
  { name: "SYNQ Strategy", tag: "Strategic Intelligence" },
  { name: "SYNQ Risk", tag: "Risk Intelligence" },
];

export default function MarketingNavbar() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [openProducts, setOpenProducts] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="marketing-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all border-b ${
        scrolled
          ? "bg-white/85 dark:bg-slate-950/85 backdrop-blur-md border-slate-200/70 dark:border-slate-800/70"
          : "bg-white/0 dark:bg-slate-950/0 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 group" data-testid="brand-link">
          <SynqLogo size={36} className="transition-transform group-hover:-rotate-3" />
          <div className="leading-none">
            <div className="font-outfit text-base font-black text-slate-900 dark:text-white tracking-tight">SYNQ</div>
            <div className="font-ibm-plex text-[9px] text-slate-500 dark:text-slate-400 mt-0.5 tracking-widest uppercase">
              Intelligence Platform
            </div>
          </div>
        </Link>

        {/* Center nav (desktop) */}
        <nav className="hidden lg:flex items-center gap-1">
          <NavLink
            to="/"
            end
            className={({ isActive }) =>
              `px-3 py-2 text-sm font-ibm-plex font-medium rounded-md transition-colors ${
                isActive ? "text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
              }`
            }
          >
            Home
          </NavLink>

          {/* Products dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOpenProducts(true)}
            onMouseLeave={() => setOpenProducts(false)}
          >
            <button
              data-testid="products-dropdown-trigger"
              onClick={() => setOpenProducts((s) => !s)}
              className="px-3 py-2 text-sm font-ibm-plex font-medium rounded-md text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white flex items-center gap-1"
            >
              Products <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openProducts ? "rotate-180" : ""}`} />
            </button>
            {openProducts && (
              <div
                data-testid="products-dropdown"
                className="absolute top-full left-1/2 -translate-x-1/2 mt-1 w-[560px] bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl shadow-slate-900/10 dark:shadow-black/40 p-3 animate-fade-in"
              >
                <div className="text-[10px] font-ibm-plex font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-500 px-3 pt-2 pb-1">
                  Intelligence Platforms
                </div>
                <div className="grid grid-cols-2 gap-1">
                  {PRODUCTS.map((p) => (
                    <Link
                      key={p.to}
                      to={p.to}
                      onClick={() => setOpenProducts(false)}
                      data-testid={`product-link-${p.name.toLowerCase().replace(/\s+/g, "-")}`}
                      className="group flex flex-col p-3 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors"
                    >
                      <div className="font-outfit font-bold text-sm text-slate-900 dark:text-white flex items-center gap-1 group-hover:gap-1.5 transition-all">
                        {p.name}
                        <ArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                      <div className="font-ibm-plex text-xs text-slate-500 dark:text-slate-400 mt-0.5">{p.tag}</div>
                    </Link>
                  ))}
                </div>
                <div className="border-t border-slate-100 dark:border-slate-800 my-2" />
                <div className="text-[10px] font-ibm-plex font-semibold tracking-widest uppercase text-slate-500 dark:text-slate-500 px-3 pt-1 pb-1">
                  Coming Soon
                </div>
                <div className="grid grid-cols-3 gap-1">
                  {COMING_SOON.map((p) => (
                    <div key={p.name} className="p-3 rounded-lg opacity-70">
                      <div className="font-outfit font-semibold text-xs text-slate-900 dark:text-white">{p.name}</div>
                      <div className="font-ibm-plex text-[10px] text-slate-500 dark:text-slate-500 mt-0.5">{p.tag}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {NAV_LINKS.slice(1).map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `px-3 py-2 text-sm font-ibm-plex font-medium rounded-md transition-colors ${
                  isActive ? "text-slate-900 dark:text-white" : "text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <ThemeToggle />
          {user ? (
            <button
              data-testid="go-to-app-btn"
              onClick={() => navigate("/dashboard")}
              className="hidden md:inline-flex items-center gap-1.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2 rounded-full font-outfit font-semibold text-sm hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
            >
              Go to App <ArrowUpRight className="w-3.5 h-3.5" />
            </button>
          ) : (
            <>
              <Link
                to="/auth"
                data-testid="sign-in-link"
                className="hidden md:inline-flex text-sm font-ibm-plex font-medium text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white px-3 py-2"
              >
                Sign In
              </Link>
              <Link
                to="/contact?intent=demo"
                data-testid="request-demo-nav-btn"
                className="hidden md:inline-flex items-center gap-1.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-2 rounded-full font-outfit font-semibold text-sm hover:bg-slate-800 dark:hover:bg-slate-100 transition-colors"
              >
                Request Demo <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
            </>
          )}

          {/* Mobile menu button */}
          <button
            data-testid="mobile-menu-btn"
            className="lg:hidden w-9 h-9 rounded-full border border-slate-200 dark:border-slate-700 flex items-center justify-center"
            onClick={() => setOpenMenu(true)}
          >
            <Menu className="w-4 h-4 text-slate-700 dark:text-slate-200" />
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      {openMenu && (
        <div data-testid="mobile-menu" className="fixed inset-0 z-50 bg-white dark:bg-slate-950">
          <div className="p-5 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
            <div className="flex items-center gap-2">
              <SynqLogo size={28} />
              <span className="font-outfit text-lg font-black text-slate-900 dark:text-white">SYNQ</span>
            </div>
            <button onClick={() => setOpenMenu(false)} className="w-9 h-9 rounded-full flex items-center justify-center border border-slate-200 dark:border-slate-700">
              <X className="w-4 h-4 text-slate-700 dark:text-slate-200" />
            </button>
          </div>
          <div className="p-5 space-y-1">
            {NAV_LINKS.map((l) => (
              <Link key={l.to} to={l.to} onClick={() => setOpenMenu(false)}
                className="block px-3 py-3 font-outfit font-semibold text-slate-900 dark:text-white text-lg border-b border-slate-100 dark:border-slate-800">
                {l.label}
              </Link>
            ))}
            <div className="pt-4">
              <div className="text-[10px] font-ibm-plex font-semibold tracking-widest uppercase text-slate-500 mb-2 px-3">Products</div>
              {PRODUCTS.map((p) => (
                <Link key={p.to} to={p.to} onClick={() => setOpenMenu(false)}
                  className="block px-3 py-2 font-ibm-plex text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white">
                  {p.name} <span className="text-xs text-slate-400">— {p.tag}</span>
                </Link>
              ))}
            </div>
            <div className="pt-6 space-y-2">
              <Link to="/contact?intent=demo" onClick={() => setOpenMenu(false)}
                className="block text-center bg-slate-900 dark:bg-white text-white dark:text-slate-900 px-4 py-3 rounded-full font-outfit font-semibold">
                Request Demo
              </Link>
              {!user && (
                <Link to="/auth" onClick={() => setOpenMenu(false)}
                  className="block text-center border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white px-4 py-3 rounded-full font-outfit font-semibold">
                  Sign In
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
